// SPLIT-mode layout: a Stage (shared screen or pinned feeds) beside/above a
// Feed strip. Orientation is chosen by comparing aspect ratios; the stage is
// maximized first (keeping its aspect exactly) while reserving room for the
// feed strip, then the stage+strip group is centered in the container.

import {
    DEFAULT_GAP,
    FeedGridResult,
    MIN_FEED_AREA_HEIGHT,
    MIN_FEED_AREA_WIDTH,
    Rect,
    Size,
    SplitOrientation,
    fitAspectInBox,
} from './types';
import { placeFeedGrid, planContentSize, planFeedGrid } from './feed-grid';

export interface SplitLayoutResult {
    orientation: SplitOrientation;
    stage: Rect;
    feedArea: Rect;
    feedGrid: FeedGridResult;
}

const emptyFeedGrid = (): FeedGridResult => ({
    cols: 0,
    rows: 0,
    tileWidth: 0,
    tileHeight: 0,
    tileAspect: 16 / 9,
    visibleCount: 0,
    overflowCount: 0,
    tiles: [],
});

export const computeSplitLayout = (
    container: Size,
    stageAspect: number,
    feedCount: number,
    options: { gap?: number; maxVisible?: number; minWidth?: number } = {},
): SplitLayoutResult => {
    const gap = options.gap ?? DEFAULT_GAP;
    const { maxVisible, minWidth } = options;
    const containerAspect = container.width / container.height;
    // Equality resolves to HORIZONTAL by convention.
    const orientation: SplitOrientation =
        containerAspect >= stageAspect ? 'HORIZONTAL' : 'VERTICAL';

    // No other feeds: the stage simply maximizes within the whole container.
    if (feedCount <= 0) {
        const size = fitAspectInBox(
            container.width,
            container.height,
            stageAspect,
        );
        return {
            orientation,
            stage: {
                x: (container.width - size.width) / 2,
                y: (container.height - size.height) / 2,
                width: size.width,
                height: size.height,
            },
            feedArea: { x: 0, y: 0, width: 0, height: 0 },
            feedGrid: emptyFeedGrid(),
        };
    }

    if (orientation === 'HORIZONTAL') {
        // Maximize the stage in (container width - reserved strip), full height.
        const stageBoxWidth = Math.max(
            1,
            container.width - MIN_FEED_AREA_WIDTH - gap,
        );
        const stage = fitAspectInBox(
            stageBoxWidth,
            container.height,
            stageAspect,
        );

        // The strip gets the leftover width; size its grid, then tighten to the
        // grid's actual content width so the group can be centered.
        const leftoverWidth = container.width - stage.width - gap;
        const plan = planFeedGrid(
            { width: leftoverWidth, height: container.height },
            feedCount,
            { gap, maxVisible, minWidth },
        );
        const content = planContentSize(plan, gap);
        const stripWidth = Math.max(content.width, MIN_FEED_AREA_WIDTH);

        const groupWidth = stage.width + gap + stripWidth;
        const groupX = Math.max(0, (container.width - groupWidth) / 2);

        const stageRect: Rect = {
            x: groupX,
            y: (container.height - stage.height) / 2,
            width: stage.width,
            height: stage.height,
        };
        const feedArea: Rect = {
            x: groupX + stage.width + gap,
            y: 0,
            width: stripWidth,
            height: container.height,
        };
        const tiles = placeFeedGrid(feedArea, plan, gap);
        return {
            orientation,
            stage: stageRect,
            feedArea,
            feedGrid: { ...gridResultFromPlan(plan), tiles },
        };
    }

    // VERTICAL: stage on top, strip on the bottom; reserve strip height.
    const stageBoxHeight = Math.max(
        1,
        container.height - MIN_FEED_AREA_HEIGHT - gap,
    );
    const stage = fitAspectInBox(container.width, stageBoxHeight, stageAspect);

    const leftoverHeight = container.height - stage.height - gap;
    const plan = planFeedGrid(
        { width: container.width, height: leftoverHeight },
        feedCount,
        { gap, maxVisible, minWidth },
    );
    const content = planContentSize(plan, gap);
    const stripHeight = Math.max(content.height, MIN_FEED_AREA_HEIGHT);

    const groupHeight = stage.height + gap + stripHeight;
    const groupY = Math.max(0, (container.height - groupHeight) / 2);

    const stageRect: Rect = {
        x: (container.width - stage.width) / 2,
        y: groupY,
        width: stage.width,
        height: stage.height,
    };
    const feedArea: Rect = {
        x: 0,
        y: groupY + stage.height + gap,
        width: container.width,
        height: stripHeight,
    };
    const tiles = placeFeedGrid(feedArea, plan, gap);
    return {
        orientation,
        stage: stageRect,
        feedArea,
        feedGrid: { ...gridResultFromPlan(plan), tiles },
    };
};

const gridResultFromPlan = (
    plan: ReturnType<typeof planFeedGrid>,
): Omit<FeedGridResult, 'tiles'> => ({
    cols: plan.cols,
    rows: plan.rows,
    tileWidth: plan.tileWidth,
    tileHeight: plan.tileHeight,
    tileAspect: plan.tileAspect,
    visibleCount: plan.visibleCount,
    overflowCount: plan.overflowCount,
});
